import { useState, useEffect, useRef } from "react";
import { toast } from "@/lib/toast";
import { Plus, Check, X, Trash2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import DeskConfirm from "@/components/crm/DeskConfirm";
import api from "@/lib/api";
import { J3Groups, ProjectLabel, PANEL } from "@/components/common/ProjectJ3";

/**
 * Picker with 3 explicit affordances :
 *  - Select-looking trigger button (opens a popover with the list)
 *  - Adjacent "+" button (Vertec green) to create a new value inline
 *  - Per-row × button (Vertec red on hover) to delete a manually-created value
 *
 * Props :
 *   endpoint         "/functions" | "/departments" | "/projects"
 *   value            controlled string (name)
 *   onChange         (name | null) => void
 *   canCreate        default true — hide the "+" for non-admins later on
 *   canDelete        default true — hide the × for non-admins later on
 *   protectedNames   optional array of names that must NOT get the ×
 *                    (system defaults). Combined with the item.created_by
 *                    check (null created_by = system).
 *   placeholder      trigger label (default "Sélectionner…")
 *   clearLabel       label of the top "clear" row (default "— Aucun —")
 *   showClear        show the clear row when a value is set (default true)
 *   testid           base test id
 */
export default function Combobox({
  endpoint,
  value,
  onChange,
  canCreate = true,
  canDelete = true,
  onCreated = null,
  protectedNames = [],
  disabled = false,
  placeholder = "Sélectionner…",
  clearLabel = "— Aucun —",
  showClear = true,
  testid = "combobox",
}) {
  const [items, setItems] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [open, setOpen] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const [pendingDelete, setPendingDelete] = useState(null);
  const isProj = endpoint === "/projects";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(endpoint);
        // iter419 pt 3 — sièges/projets : seuls ceux « proposés dans les choix » (corbeille jamais renvoyée par l'API)
        if (!cancelled) setItems(endpoint === "/projects" ? (data || []).filter((p) => p.in_choices !== false) : (data || []));
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [endpoint, refreshTick]);

  useEffect(() => {
    if (creating) requestAnimationFrame(() => inputRef.current?.focus());
  }, [creating]);

  const startCreate = () => {
    setNewValue("");
    setCreating(true);
    setOpen(false);
  };
  const cancelCreate = () => {
    setCreating(false);
    setNewValue("");
  };
  const confirmCreate = async () => {
    const name = newValue.trim();
    if (!name) return;
    setSaving(true);
    try {
      const { data } = await api.post(endpoint, { name });
      onChange(data.name);
      onCreated?.(data);
      setRefreshTick((t) => t + 1);
      setCreating(false);
      setNewValue("");
    } catch (err) {
      toast.error("Création refusée", {
        description: err.response?.data?.detail || "Réessayez.",
      });
    } finally { setSaving(false); }
  };

  const pick = (name) => {
    onChange(name);
    setOpen(false);
  };
  const clear = () => {
    onChange(null);
    setOpen(false);
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`${endpoint}/${pendingDelete.id}`);
      toast.success(`« ${pendingDelete.name} » supprimé`);
      if (value === pendingDelete.name) onChange(null);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error("Suppression refusée", {
        description: err.response?.data?.detail || "Réessayez.",
      });
    } finally { setPendingDelete(null); }
  };

  const isProtected = (item) =>
    !item.created_by || protectedNames.includes(item.name);

  /* ------------------------------------------------------------ Creating UI */
  if (creating) {
    return (
      <div className="flex gap-2" data-testid={`${testid}-creating`}>
        <Input
          ref={inputRef}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); confirmCreate(); }
            if (e.key === "Escape") { e.preventDefault(); cancelCreate(); }
          }}
          placeholder="Nom à créer…"
          disabled={saving}
          className="flex-1"
          data-testid={`${testid}-new-input`}
        />
        <Button
          type="button" size="icon"
          onClick={confirmCreate}
          disabled={saving || !newValue.trim()}
          className="bg-vertec-green hover:bg-vertec-greenHover text-white shrink-0"
          aria-label="Valider"
          data-testid={`${testid}-confirm`}
        >
          <Check size={16} />
        </Button>
        <Button
          type="button" size="icon" variant="outline"
          onClick={cancelCreate}
          disabled={saving}
          className="border-vertec-borderDark shrink-0"
          aria-label="Annuler"
          data-testid={`${testid}-cancel`}
        >
          <X size={16} />
        </Button>
      </div>
    );
  }

  /* ---------------------------------------------------------- Trigger + list */
  return (
    <>
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              disabled={disabled}
              className="flex-1 justify-between border-vertec-borderDark font-normal"
              data-testid={testid}
            >
              {isProj ? <ProjectLabel p={items.find((d) => d.name === value) || (value ? { name: value } : null)} placeholder={placeholder} /> : <span className={value ? "" : "text-muted-foreground"}>
                {value || placeholder}
              </span>}
              <ChevronDown size={14} className="opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={isProj ? "w-[--radix-popover-trigger-width] p-0 border-0 bg-transparent shadow-none" : "w-[--radix-popover-trigger-width] p-1"}
            align="start"
          >
            {isProj ? (
              <div style={{ ...PANEL, maxHeight: 360, overflowY: "auto" }} data-testid={`${testid}-list`}>
                {showClear && value && <><button type="button" onClick={clear} className="w-full text-left hover:bg-[#26262C]" style={{ height: 34, padding: "0 10px", borderRadius: 6, fontSize: 12.5, color: "#8A8A93", fontStyle: "italic" }} data-testid={`${testid}-clear`}>{clearLabel}</button><div style={{ height: 1, background: "#2A2A30", margin: "3px 0" }} /></>}
                <J3Groups projects={items} isSelected={(p) => p.name === value} onPick={(p) => pick(p.name)} testIdPrefix={testid} />
              </div>
            ) : (
            <div className="max-h-64 overflow-auto py-1" data-testid={`${testid}-list`}>
              {showClear && value && (
                <button
                  type="button"
                  onClick={clear}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-accent text-muted-foreground italic"
                  data-testid={`${testid}-clear`}
                >
                  <span className="w-4" />
                  {clearLabel}
                </button>
              )}
              {items.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Aucun élément.
                  {canCreate && (
                    <>
                      <br />
                      Cliquez sur{" "}
                      <span className="inline-flex items-center align-middle rounded-sm bg-vertec-green text-white px-1">
                        <Plus size={10} />
                      </span>{" "}
                      pour en créer.
                    </>
                  )}
                </div>
              )}
              {items.map((d, i) => {
                const selected = value === d.name;
                const removable = canDelete && !isProtected(d);
                const sepAfter = d.kind === "siege" && items[i + 1] && items[i + 1].kind !== "siege"; // iter419 pt 4 — trait Siège social / projets
                return (
                  <div
                    key={d.id}
                    style={sepAfter ? { borderBottom: "1px solid #2a2a30", marginBottom: 4, paddingBottom: 6, borderRadius: 0 } : undefined}
                    data-siege-sep={sepAfter || undefined}
                    className="group flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent"
                    data-testid={`${testid}-item-${d.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => pick(d.name)}
                      className="flex-1 flex items-center gap-2 text-left text-sm"
                    >
                      <Check
                        size={14}
                        className={selected ? "opacity-100 text-vertec-blue" : "opacity-0"}
                      />
                      <span className="truncate">{d.name}</span>
                    </button>
                    {removable && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(d); }}
                        aria-label={`Supprimer ${d.name}`}
                        title="Supprimer"
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-status-danger hover:bg-status-danger/10"
                        data-testid={`${testid}-delete-${d.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </PopoverContent>
        </Popover>

        {canCreate && (
          <Button
            type="button"
            size="icon"
            onClick={startCreate}
            disabled={disabled}
            className="bg-vertec-green hover:bg-vertec-greenHover text-white shrink-0"
            aria-label="Créer un nouveau"
            title="Créer un nouveau"
            data-testid={`${testid}-plus`}
          >
            <Plus size={16} />
          </Button>
        )}
      </div>

      <DeskConfirm open={!!pendingDelete} title="Supprimer cette valeur ?"
        message={`« ${pendingDelete?.name || ""} » sera retiré de la liste. Les utilisateurs qui y étaient rattachés verront le champ vidé. Cette action est définitive.`}
        confirmLabel="Supprimer" onClose={() => setPendingDelete(null)} onConfirm={doDelete} testId={`${testid}-confirm-delete`} />
    </>
  );
}
